import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Container from '@mui/material/Container';
import Table from '@mui/material/Table';
import TablePagination from '@mui/material/TablePagination';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import FilterListIcon from '@mui/icons-material/FilterList';
import Skeleton from '@mui/material/Skeleton';
import Chip from '@mui/material/Chip';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Users from '../../collections/users';
import Packages from '../../collections/packages';
import {
  financialStatusTranslations,
  userPackageStatusTranslations,
  sendingInvoiceStatusTranslations,
  getFinancialStatusColor,
  getUserPackageStatusColor,
  getSendingInvoiceStatusColor
} from '../../utils/statusTranslations';

import UserPackages from '../../collections/user_packages';


const UserPackagesList = (props) => {
  const navigate = useNavigate();
  const [userPackages, setUserPackages] = useState(new UserPackages());
  const [loading, setLoading] = useState(true);
  const [mounted, setMounted] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(() => {
    const pageParam = searchParams.get('page');
    return pageParam ? parseInt(pageParam, 10) - 1 : 0;
  });
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    const perPageParam = searchParams.get('per_page');
    return perPageParam ? parseInt(perPageParam, 10) : 25;
  });
  const [selectedUserId, setSelectedUserId] = useState(searchParams.get('user_id') || '');
  const [selectedPackageId, setSelectedPackageId] = useState(searchParams.get('package_id') || '');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') || '');
  const [selectedFinancialStatus, setSelectedFinancialStatus] = useState(searchParams.get('financial_status') || '');
  const [selectedSendingInvoiceStatus, setSelectedSendingInvoiceStatus] = useState(searchParams.get('sending_invoice_status') || '');
  const [maxMonthlyConvertedQuantity, setMaxMonthlyConvertedQuantity] = useState(searchParams.get('max_monthly_converted_quantity') || '');
  const [users, setUsers] = useState([]);
  const [packages, setPackages] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    props.setPageTitle('Danh sách gói dịch vụ của khách hàng');
  }, [props]);

  const fetchUsers = async () => {
    const usersList = new Users();
    await usersList.fetch(1, 100);
    setUsers(usersList.items);
  };

  const fetchPackages = async () => {
    const packagesList = new Packages();
    await packagesList.fetch({ per_page: 100 });
    setPackages(packagesList.items);
  };

  const fetchData = async () => {
    setLoading(true);
    const params = {
      user_id: selectedUserId,
      package_id: selectedPackageId,
      status: selectedStatus,
      financial_status: selectedFinancialStatus,
      sending_invoice_status: selectedSendingInvoiceStatus,
      max_monthly_converted_quantity: maxMonthlyConvertedQuantity
    };
    await userPackages.fetch(page + 1, rowsPerPage, params);
    setTotal(userPackages.total);
    setLoading(false);
  };

  useEffect(() => {
    if (mounted) return;
    setMounted(true);

    // Initialize from URL params
    const pageParam = searchParams.get('page');
    const perPageParam = searchParams.get('per_page');
    const statusParam = searchParams.get('status');
    const financialStatusParam = searchParams.get('financial_status');
    const sendingInvoiceStatusParam = searchParams.get('sending_invoice_status');

    if (pageParam) {
      setPage(parseInt(pageParam, 10) - 1);
    }
    if (perPageParam) {
      setRowsPerPage(parseInt(perPageParam, 10));
    }
    if (statusParam) {
      setSelectedStatus(statusParam);
    }
    if (financialStatusParam) {
      setSelectedFinancialStatus(financialStatusParam);
    }
    if (sendingInvoiceStatusParam) {
      setSelectedSendingInvoiceStatus(sendingInvoiceStatusParam);
    }

    fetchUsers();
    fetchPackages();
    fetchData();
  }, [mounted, searchParams]);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [page, rowsPerPage, selectedUserId, selectedPackageId, selectedStatus, selectedFinancialStatus, selectedSendingInvoiceStatus, maxMonthlyConvertedQuantity]);

  const handleChangePage = async (event, newPage) => {
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: (newPage + 1).toString(),
      per_page: rowsPerPage.toString()
    });
    setPage(newPage);
  };

  const handleChangeRowsPerPage = async (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setSearchParams({
      ...Object.fromEntries(searchParams),
      page: '1',
      per_page: newRowsPerPage.toString()
    });
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handlePrebuiltFilter = () => {
    const isActive = maxMonthlyConvertedQuantity === '2';
    if (isActive) {
      // Clear the prebuilt filter
      setMaxMonthlyConvertedQuantity('');
      setSelectedSendingInvoiceStatus('');
      setSearchParams({
        ...Object.fromEntries(searchParams),
        max_monthly_converted_quantity: '',
        sending_invoice_status: '',
        page: '1'
      });
    } else {
      // Apply the prebuilt filter
      setMaxMonthlyConvertedQuantity('2');
      setSelectedSendingInvoiceStatus('PENDING');
      setSearchParams({
        ...Object.fromEntries(searchParams),
        max_monthly_converted_quantity: '2',
        sending_invoice_status: 'PENDING',
        page: '1'
      });
    }
    setPage(0);
  };

  if (loading) {
    return (
      <Skeleton
        animation="wave"
        variant="rectangular"
        height={250}
        sx={{ m: 3 }}
      />
    );
  }

  return (
    <Container sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          variant="outlined"
          size="small"
          sx={{ textTransform: 'none' }}
          startIcon={<AddIcon />}
          onClick={() => navigate('/user-packages/create')}
        >
          Thêm gói dịch vụ
        </Button>

        <Chip
          icon={<FilterListIcon />}
          label="Chưa gửi báo cáo & Số lượng còn lại < 2kg"
          onClick={handlePrebuiltFilter}
          color={maxMonthlyConvertedQuantity === '2' ? "warning" : "default"}
          variant={maxMonthlyConvertedQuantity === '2' ? "filled" : "outlined"}
          sx={{
            fontWeight: maxMonthlyConvertedQuantity === '2' ? 'bold' : 'normal',
            cursor: 'pointer',
            '&:hover': {
              backgroundColor: maxMonthlyConvertedQuantity === '2' ? 'warning.dark' : 'action.hover',
            }
          }}
          onDelete={maxMonthlyConvertedQuantity === '2' ? handlePrebuiltFilter : undefined}
        />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <Autocomplete
            size="small"
            options={users}
            getOptionLabel={(option) => option.name || ''}
            filterOptions={(x) => x}
            value={users.find(user => user.id === selectedUserId) || null}
            onChange={(event, newValue) => {
              setSelectedUserId(newValue ? newValue.id : '');
              setSearchParams({
                ...Object.fromEntries(searchParams),
                user_id: newValue ? newValue.id : '',
                page: '1'
              });
              setPage(0);
            }}
            onInputChange={async (event, newInputValue) => {
              if (newInputValue) {
                const usersList = new Users();
                await usersList.fetch(1, 100, {}, newInputValue);
                setUsers(usersList.items);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Khách hàng (tên hoặc SĐT)"
                variant="outlined"
              />
            )}
          />
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Gói dịch vụ</InputLabel>
          <Select
            value={selectedPackageId}
            label="Gói dịch vụ"
            onChange={(e) => {
              setSelectedPackageId(e.target.value);
              setSearchParams({
                ...Object.fromEntries(searchParams),
                package_id: e.target.value,
                page: '1'
              });
              setPage(0);
            }}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {packages.map((pkg) => (
              <MenuItem key={pkg.id} value={pkg.id}>
                {pkg.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Trạng thái</InputLabel>
          <Select
            value={selectedStatus}
            label="Trạng thái"
            onChange={(e) => {
              setSelectedStatus(e.target.value);
              setSearchParams({
                ...Object.fromEntries(searchParams),
                status: e.target.value,
                page: '1'
              });
              setPage(0);
            }}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {Object.entries(userPackageStatusTranslations).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Trạng thái gửi báo cáo</InputLabel>
          <Select
            value={selectedSendingInvoiceStatus}
            label="Trạng thái gửi báo cáo"
            onChange={(e) => {
              setSelectedSendingInvoiceStatus(e.target.value);
              setSearchParams({
                ...Object.fromEntries(searchParams),
                sending_invoice_status: e.target.value,
                page: '1'
              });
              setPage(0);
            }}
          >
            <MenuItem value="">Tất cả</MenuItem>
            {Object.entries(sendingInvoiceStatusTranslations).map(([key, value]) => (
              <MenuItem key={key} value={key}>
                {value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* <FormControl size="small" sx={{ minWidth: 180 }}> */}
        {/*   <InputLabel>Trạng thái thanh toán</InputLabel> */}
        {/*   <Select */}
        {/*     value={selectedFinancialStatus} */}
        {/*     label="Trạng thái thanh toán" */}
        {/*     onChange={(e) => { */}
        {/*       setSelectedFinancialStatus(e.target.value); */}
        {/*       setSearchParams({ */}
        {/*         ...Object.fromEntries(searchParams), */}
        {/*         financial_status: e.target.value, */}
        {/*         page: '1' */}
        {/*       }); */}
        {/*       setPage(0); */}
        {/*     }} */}
        {/*   > */}
        {/*     <MenuItem value="">Tất cả</MenuItem> */}
        {/*     {Object.entries(financialStatusTranslations).map(([key, value]) => ( */}
        {/*       <MenuItem key={key} value={key}> */}
        {/*         {value} */}
        {/*       </MenuItem> */}
        {/*     ))} */}
        {/*   </Select> */}
        {/* </FormControl> */}
      </Box >

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Khách hàng</TableCell>
              <TableCell>Địa chỉ</TableCell>
              <TableCell>Gói</TableCell>
              <TableCell>Ngày bắt đầu</TableCell>
              <TableCell>Ngày kết thúc</TableCell>
              <TableCell>Trạng thái</TableCell>
              <TableCell>Trạng thái thanh toán</TableCell>
              <TableCell>Trạng thái gửi báo cáo</TableCell>
              <TableCell>Số lượng sản phẩm (đã quy đổi)</TableCell>
              <TableCell>Số lượng còn lại</TableCell>
              <TableCell>Đơn hàng cuối</TableCell>
              <TableCell>Ghi chú</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {userPackages.items.map((userPackage) => (
              <TableRow
                key={userPackage.id}
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                }}
                onClick={() => navigate(`/user-packages/${userPackage.id}`)}
              >
                <TableCell>{userPackage.id}</TableCell>
                <TableCell>{userPackage.user?.name}</TableCell>
                <TableCell>{userPackage.user?.address || '-'}</TableCell>
                <TableCell>{userPackage.package?.name}</TableCell>
                <TableCell>{dayjs(userPackage.beginning_date).format('DD/MM/YYYY')}</TableCell>
                <TableCell>
                  {userPackage.ending_date
                    ? dayjs(userPackage.ending_date).format('DD/MM/YYYY')
                    : '-'}
                </TableCell>
                <TableCell>
                  <Chip
                    label={userPackageStatusTranslations[userPackage.status]}
                    color={getUserPackageStatusColor(userPackage.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={financialStatusTranslations[userPackage.financial_status]}
                    color={getFinancialStatusColor(userPackage.financial_status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={sendingInvoiceStatusTranslations[userPackage.sending_invoice_status]}
                    color={getSendingInvoiceStatusColor(userPackage.sending_invoice_status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {(() => {
                    // Calculate total quantities by order type
                    if (userPackage.orders && Array.isArray(userPackage.orders) && userPackage.orders.length > 0) {
                      // Group and sum quantities by order_type
                      const quantitiesByType = userPackage.orders
                      .filter(order => order.status !== 'CANCELLED')
                      .flatMap(order => order.order_line_items || [])
                      .filter(item => item.quantity > 0)
                      .reduce((acc, lineItem) => {
                        const type = lineItem.order_type || 'UNKNOWN';
                        if (!acc[type]) {
                          acc[type] = {
                            actual: 0,
                            converted: 0
                          };
                        }
                        acc[type].actual += (lineItem.quantity || 0);
                        // For MONTHLY_PACKAGE_ORDER: only convert if convert_rate is truthy
                        // For INSTANT_ORDER: use raw quantity (no conversion)
                        if (type === 'MONTHLY_PACKAGE_ORDER') {
                          if (lineItem.convert_rate) {
                            acc[type].converted += (lineItem.quantity / lineItem.convert_rate);
                          }
                        } else {
                          acc[type].converted += lineItem.quantity;
                        }
                        return acc;
                      }, {});

                      // Order type translation mapping
                      const orderTypeTranslations = {
                        'MONTHLY_PACKAGE_ORDER': 'Gói tháng',
                        'INSTANT_ORDER': 'Mua lẻ',
                        'UNKNOWN': 'Không xác định'
                      };

                      // Format the result to show both actual and converted quantities
                      return Object.entries(quantitiesByType)
                        .map(([type, quantities]) => {
                          const translatedType = orderTypeTranslations[type] || type;
                          // Format converted quantity with 2 decimal places
                          const displayConverted = parseFloat(quantities.converted.toFixed(2));

                          return (
                            <Chip
                              key={type}
                              label={`${translatedType}: ${displayConverted} kg`}
                              size="small"
                              sx={{ mr: 0.5, mb: 0.5 }}
                              title={`Số lượng thực nhận: ${quantities.actual}`}
                            />
                          );
                        });
                    }
                    return '-';
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    // Calculate remaining quantity: package.amount + bonus_total - used_monthly_quantity
                    const packageAmount = userPackage.package?.amount || 0;
                    const bonusTotal = userPackage.bonus_total || 0;
                    const totalAvailable = packageAmount + bonusTotal;

                    if (userPackage.orders && Array.isArray(userPackage.orders) && userPackage.orders.length > 0) {
                      // Calculate used quantity for MONTHLY_PACKAGE_ORDER only
                      // Only count items with convert_rate (truthy)
                      const usedQuantity = userPackage.orders
                        .filter(order => order.status !== 'CANCELLED')
                        .flatMap(order => order.order_line_items || [])
                        .filter(item => item.order_type === 'MONTHLY_PACKAGE_ORDER' && item.quantity > 0)
                        .filter(item => item.convert_rate)
                        .reduce((sum, lineItem) => {
                          return sum + (lineItem.quantity / lineItem.convert_rate);
                        }, 0);

                      const remaining = totalAvailable - usedQuantity;
                      const displayRemaining = parseFloat(remaining.toFixed(2));

                      // Color code: red if < 2, orange if < 5, green otherwise
                      let color = 'success';
                      if (remaining < 2) {
                        color = 'error';
                      } else if (remaining < 5) {
                        color = 'warning';
                      }

                      const tooltipText = bonusTotal > 0
                        ? `Gói: ${packageAmount} kg | Tặng: +${bonusTotal.toFixed(2)} kg | Tổng: ${totalAvailable.toFixed(2)} kg | Đã dùng: ${parseFloat(usedQuantity.toFixed(2))} kg`
                        : `Tổng gói: ${packageAmount} kg | Đã dùng: ${parseFloat(usedQuantity.toFixed(2))} kg`;

                      return (
                        <Chip
                          label={`${displayRemaining} kg`}
                          size="small"
                          color={color}
                          title={tooltipText}
                        />
                      );
                    }

                    // If no orders, remaining = full package amount + bonus
                    const tooltipText = bonusTotal > 0
                      ? `Gói: ${packageAmount} kg | Tặng: +${bonusTotal.toFixed(2)} kg`
                      : 'Chưa có đơn hàng';

                    return (
                      <Chip
                        label={`${totalAvailable.toFixed(2)} kg`}
                        size="small"
                        color="default"
                        title={tooltipText}
                      />
                    );
                  })()}
                </TableCell>
                <TableCell>
                  {(() => {
                    // If we have userPackage.orders data available from the API
                    if (userPackage.orders && Array.isArray(userPackage.orders) && userPackage.orders.length > 0) {
                      // Find the most recent processed_at date from all non-cancelled orders
                      const maxProcessedAt = userPackage.orders
                      .filter(order => order.status !== 'CANCELLED' && order.processed_at)
                      .reduce((maxDate, order) => {
                        const orderDate = dayjs(order.processed_at);
                        return !maxDate || orderDate.isAfter(maxDate) ? orderDate : maxDate;
                      }, null);

                      return maxProcessedAt ? maxProcessedAt.format('DD/MM/YYYY') : '-';
                    } 
                    // Fallback to last_order_at if orders data isn't available
                    else {
                      return userPackage.last_order_at 
                        ? dayjs(userPackage.last_order_at).format('DD/MM/YYYY') 
                        : '-';
                    }
                  })()}
                </TableCell>
                <TableCell>{userPackage.note || '-'}</TableCell>
              </TableRow >
            ))}
          </TableBody >
        </Table >
      </TableContainer >
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50]}
      />
    </Container >
  );
};

export default UserPackagesList;
